import { Controller } from "@hotwired/stimulus";

// Tabs controller: manages activation and lazy iframe src assignment
export default class extends Controller {
  static targets = ["tab", "panel", "iframe"];

  defaultActiveClasses = ["bg-white", "text-grey-900"];
  defaultInactiveClasses = ["bg-grey-50", "text-grey-700"];

  connect() {
    // Ensure first tab is active
    this.activateByIndex(this.activeIndex() ?? 0, { focusTab: false });
  }

  onKeydown(event) {
    const key = event.key;
    const idx = Number(event.currentTarget.dataset.index);
    if (key === "ArrowRight") {
      event.preventDefault();
      this.activateByIndex((idx + 1) % this.tabTargets.length, { focusTab: true });
    } else if (key === "ArrowLeft") {
      event.preventDefault();
      this.activateByIndex((idx - 1 + this.tabTargets.length) % this.tabTargets.length, { focusTab: true });
    }
  }

  activate(event) {
    const idx = Number(event.currentTarget.dataset.index);
    // Click already focuses the tab button naturally
    this.activateByIndex(idx, { focusTab: false });
  }

  activateByIndex(index, { focusTab } = { focusTab: false }) {
    // Toggle aria-selected on tabs
    this.tabTargets.forEach((el, i) => {
      const activeClasses = this.classListFromDataset(el.dataset.tabsActiveClasses, this.defaultActiveClasses);
      const inactiveClasses = this.classListFromDataset(el.dataset.tabsInactiveClasses, this.defaultInactiveClasses);

      el.setAttribute("aria-selected", i === index ? "true" : "false");

      [...activeClasses, ...inactiveClasses].forEach((className) => {
        el.classList.remove(className);
      });

      el.classList.add(...(i === index ? activeClasses : inactiveClasses));
    });

    if (focusTab) {
      const tab = this.tabTargets[index];
      if (tab) {
        try {
          tab.focus({ preventScroll: true });
        } catch (e) {
          tab.focus();
        }
      }
    }

    // Show/hide panels
    this.panelTargets.forEach((panel, i) => {
      if (i === index) {
        panel.removeAttribute("hidden");
        // Lazy set iframe src on first activation
        panel.querySelectorAll("iframe[data-src]").forEach((iframe) => {
          if (!iframe.getAttribute("src")) {
            iframe.setAttribute("src", iframe.dataset.src);
          }
        });
      } else {
        panel.setAttribute("hidden", "");
      }
    });
  }

  activeIndex() {
    const selected = this.tabTargets.findIndex((el) => el.getAttribute("aria-selected") === "true");
    return selected >= 0 ? selected : 0;
  }

  classListFromDataset(value, fallback) {
    if (!value) {
      return fallback;
    }

    const classes = value.split(" ").map((className) => className.trim()).filter(Boolean);
    return classes.length ? classes : fallback;
  }
}
